import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import type { SocialCatalogStatus, SocialProvider } from '@nucleus-commerce/api-client/admin';

import { socialApi } from '../api/social-api';

import { socialKeys } from './social-keys';

/** Every catalogue call answers the fresh status; the connection row carries the sync flag too. */
function settle(
  queryClient: QueryClient,
  storeId: string,
  provider: SocialProvider,
  status: SocialCatalogStatus,
) {
  queryClient.setQueryData<SocialCatalogStatus>(socialKeys.catalog(storeId, provider), status);
  return queryClient.invalidateQueries({ queryKey: socialKeys.connections(storeId) });
}

/** Registers the store's feed URL with the network and asks for a first fetch. */
export function useEnableCatalog(storeId: string, provider: SocialProvider) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => socialApi.enableCatalog(storeId, provider),
    meta: { successMessage: 'social:toasts.catalogEnabled' },
    onSuccess: (status) => settle(queryClient, storeId, provider, status),
  });
}

/** Deletes the feed on the network; feed links stop carrying UTM. */
export function useDisableCatalog(storeId: string, provider: SocialProvider) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => socialApi.disableCatalog(storeId, provider),
    meta: { successMessage: 'social:toasts.catalogDisabled' },
    onSuccess: (status) => settle(queryClient, storeId, provider, status),
  });
}

/** "Sync now": the network fetches the feed ahead of its daily schedule. */
export function useIngestCatalog(storeId: string, provider: SocialProvider) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => socialApi.ingestCatalog(storeId, provider),
    meta: { successMessage: 'social:toasts.syncRequested' },
    onSuccess: (status) => settle(queryClient, storeId, provider, status),
  });
}
